import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { browserSupportsWebAuthnAutofill, startAuthentication, WebAuthnAbortService } from "@simplewebauthn/browser";
import { KeyRound, Loader2, Mail, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { ActionButton, TextLink } from "../components/kit";
import { TAP_LINK_STYLE, tapLine } from "../components/kit/tapTarget";
import api from "../lib/api";
import { safeRedirectTarget } from "../lib/redirectTarget";
import { homePathForRole } from "../lib/homePath";
import { SESSION_ENDED_MESSAGE, sessionEndedRequested, FRONT_DOOR_PATH, SIGN_UP_PATH } from "../lib/sessionExpiry";
import { BrandMark } from "../components/brand/BrandMark";

/**
 * SignInNew — issue #874, slice S1, sign-in redrawn to `SignIn.dc.html`
 * (design-baseline-round-5). Lives behind `newUi("signin")` (`App.js`);
 * `Login.jsx`, the shipped screen, is untouched and keeps serving `/login`
 * with the flag off.
 *
 * PRESENTATION ONLY — every handler below is the SAME call Login.jsx makes:
 * `login`/`completePasskeySignIn` from `useAuth()`, the same
 * `@simplewebauthn/browser` ceremony (`startAuthentication`,
 * `WebAuthnAbortService`, the conditional-UI effect started on mount), the
 * same `safeRedirectTarget`/`homePathForRole` redirect, and the same
 * `sessionEndedRequested` check and `SESSION_ENDED_MESSAGE` constant.
 * Nothing here calls a different endpoint, and no testid the drawing names
 * is dropped.
 *
 * D205 (Andreas, 2026-10-01) — NO PUBLIC DEMO. The demo callout that used
 * to sit below the sign-in form (`exploreAsResult`, `demoFailMessage`,
 * `exploring`, both buttons) is removed: people must
 * register and sign in, there is no public demo door. `exploreAsResult`/
 * `exploreAs` still live in `AuthContext.jsx` for `Layout.jsx`'s
 * persona-switch banner — a signed-in affordance, not a public one, and
 * #1023 keeps it.
 *
 * D48/D57 — A failed PASSWORD sign-in no longer raises a toast: `failed`
 * (below) drives an inline message that stays on the page — the drawing's
 * own reasoning is that a toast cannot be reviewed, and the person reading
 * it is about to retype a password. The passkey ceremony's own failure
 * now stays on the page too (#1078, D57): the same `login-error` slot, with
 * the passkey sentence. Dismissing the browser's own sheet is deliberately
 * silent either way (see
 * `finishPasskeySignIn`/`signInWithPasskey` below).
 *
 * D109 — "Welcome back!" is unchanged: it is the existing sign-in success
 * toast, it lands on `/dashboard` (which does not itself confirm sign-in),
 * and `arrivalToastGate.js`'s own suppression is scoped to `newUi("record")`
 * arrivals at `/dashboard` from a FRESH signup/onboarding-finish — never
 * from a sign-in. This screen never calls `arrivalToastSuppressed`.
 */

const PASSKEY_FAILED_MESSAGE = "Passkey sign-in failed. Try again or use your password.";

// #1392 (D221 G4): the two short links on this screen get a 44px tap box around an unchanged label.
// The brand row is a 24px line at the top of the stack: its box grows 10px each way and its margin box
// stays the 24px it was laid out with.
const BRAND_LINK_BOX = { ...TAP_LINK_STYLE, marginBlock: "calc((24px - var(--nw-size-tap)) / 2)" };
// "Create one" sits one stack gap (14px) under "Forgot your password?", whose own box reaches 12px below
// its label. A symmetric box would reach 12px above this label and cover the lower part of that one, so
// this box grows downward only: the label stays at the top of it and the margin box stays one line.
const DOWNWARD_TAP_BOX = { ...TAP_LINK_STYLE, alignItems: "flex-start", marginBlock: `0 calc(${tapLine()} - var(--nw-size-tap))` };

export default function SignInNew() {
  const { user, login, error, completePasskeySignIn } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const target = safeRedirectTarget(location.state?.from, homePathForRole(user?.role));
  const sessionEnded = sessionEndedRequested(location.search);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  // D48/D57: whether the LAST password submit failed — not the shared
  // AuthContext `error` string alone, which the passkey ceremony also
  // writes. Cleared at the start of every new attempt so a stale banner can
  // never survive past the action that would replace it.
  const [failed, setFailed] = useState(false);
  // D57: the passkey ceremony's own failure. Same on-page slot as `failed`
  // (one `login-error`), its own sentence; cleared when either door starts
  // a new attempt.
  const [passkeyFailed, setPasskeyFailed] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const passkeySupported = typeof window !== "undefined" && !!window.PublicKeyCredential;
  const mountedRef = useRef(true);

  const finishPasskeySignIn = async (assertion) => {
    const signedIn = await completePasskeySignIn(assertion);
    if (!mountedRef.current) return;
    if (signedIn) {
      toast.success("Welcome back!");
      nav(safeRedirectTarget(location.state?.from, homePathForRole(signedIn.role)), { replace: true });
    } else {
      setPasskeyFailed(true);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        if (!passkeySupported || !(await browserSupportsWebAuthnAutofill())) return;
        const { data: options } = await api.post("/auth/passkey/authenticate/options", {});
        if (cancelled) return;
        const assertion = await startAuthentication({ optionsJSON: options, useBrowserAutofill: true });
        await finishPasskeySignIn(assertion);
      } catch {
        // AbortError (we cancelled it) or NotAllowedError (dismissed): no
        // toast, no page state — the drawing's own "dismissing it is not an
        // error and says nothing on this screen".
      }
    })();
    return () => {
      mountedRef.current = false;
      cancelled = true;
      WebAuthnAbortService.cancelCeremony();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signInWithPasskey = async () => {
    setFailed(false);
    setPasskeyFailed(false);
    setPasskeyBusy(true);
    try {
      WebAuthnAbortService.cancelCeremony();
      const { data: options } = await api.post("/auth/passkey/authenticate/options", {});
      const assertion = await startAuthentication({ optionsJSON: options });
      await finishPasskeySignIn(assertion);
    } catch (e) {
      if (e?.name !== "NotAllowedError" && e?.name !== "AbortError") {
        if (mountedRef.current) setPasskeyFailed(true);
      }
    }
    if (mountedRef.current) setPasskeyBusy(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setFailed(false);
    setPasskeyFailed(false);
    WebAuthnAbortService.cancelCeremony();
    const signedIn = await login(email, password, remember);
    setBusy(false);
    if (signedIn) {
      toast.success("Welcome back!");
      nav(safeRedirectTarget(location.state?.from, homePathForRole(signedIn.role)), { replace: true });
    } else {
      // D48/D57: stays on the page, no toast.
      setFailed(true);
    }
  };

  if (user) return <Navigate to={target} replace />;

  return (
    <div style={{ minHeight: "100vh", background: "var(--nw-page)" }}>
      {/* Round 5 conformance (token-check) — every element the drawing
          names declares its own ink token rather than inheriting the
          shell's `body` colour (`#0B1220`, not `--nw-ink` `#0F172A`), the
          same rule the kit's type-ramp README states for `.nw-*` classes.
          Harmless today on these four (this marker, the header Link, the
          form and the checkbox all have zero visible text of their own —
          every real text node underneath already declares its own
          colour), declared anyway rather than left to coincide. */}
      <span data-testid="signin-new-screen" className="sr-only" style={{ color: "var(--nw-ink)" }} aria-hidden="true" />
      <div
        className="nw-column"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--nw-space-stack)",
          padding: "var(--nw-space-page)",
        }}
      >
        <Link to={FRONT_DOOR_PATH} data-testid="link-landing" style={{ ...BRAND_LINK_BOX, display: "flex", gap: 8, color: "var(--nw-ink)", textDecoration: "none" }}>
          <BrandMark size={24} />
          <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 16, color: "var(--nw-navy)" }}>
            Compass
          </span>
        </Link>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div className="nw-title">Sign in</div>
          <div className="nw-lede">
            Welcome back — let's continue your journey.{" "}
            <TextLink as={Link} inline to={FRONT_DOOR_PATH} style={{ fontWeight: 600, color: "var(--nw-navy)", textDecoration: "underline" }}>
              What is CricketCompass?
            </TextLink>
          </div>
        </div>

        {sessionEnded && (
          <div
            role="status"
            data-testid="session-ended-notice"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              padding: "10px 14px",
              background: "var(--nw-surface)",
              border: "1px dashed var(--nw-line-strong)",
              borderRadius: "var(--nw-radius-card)",
            }}
          >
            {/* Round 5 review, B1 — the eyebrow "Sent here because the
                session ended" is the drawing's own muted heading, inside
                this `data-dc-note` block: it names the state to whoever
                reads the drawing, not copy for a real visitor. The message
                box below is the real, functionally necessary UI (the same
                `SESSION_ENDED_MESSAGE`/`session-ended-notice` journey 08
                already asserts on the shipped screen, testid/role kept on
                the outer wrapper, unchanged) and stays. */}
            <div
              style={{
                padding: "10px 12px",
                background: "var(--nw-line-faint)",
                border: "1px solid var(--nw-line)",
                borderRadius: "var(--nw-radius-control)",
                fontSize: 13,
                lineHeight: 1.5,
                color: "var(--nw-ink)",
              }}
            >
              {SESSION_ENDED_MESSAGE}
            </div>
          </div>
        )}

        <form
          onSubmit={submit}
          data-testid="login-form"
          style={{ display: "flex", flexDirection: "column", gap: "var(--nw-space-stack)", color: "var(--nw-ink)" }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {/* Review round 3, final narrow round (A3), F9: real
                <label htmlFor>, same fix as kit/Field.jsx's own
                (#902/#907) — explicit display: "block" keeps the layout
                byte-identical. */}
            <label htmlFor="login-email" className="nw-label" style={{ display: "block" }}>Email</label>
            <input
              id="login-email"
              data-testid="login-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              autoComplete="username webauthn"
              required
              className="nw-kit-input"
              style={{
                width: "100%",
                height: "var(--nw-size-control)",
                borderRadius: "var(--nw-radius-control)",
                border: "1px solid var(--nw-line)",
                background: "var(--nw-surface)",
                padding: "0 12px",
                fontSize: 15,
                color: "var(--nw-ink)",
              }}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label htmlFor="login-password" className="nw-label" style={{ display: "block" }}>Password</label>
            <input
              id="login-password"
              data-testid="login-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type="password"
              required
              className="nw-kit-input"
              style={{
                width: "100%",
                height: "var(--nw-size-control)",
                borderRadius: "var(--nw-radius-control)",
                border: "1px solid var(--nw-line)",
                background: "var(--nw-surface)",
                padding: "0 12px",
                fontSize: 15,
                color: "var(--nw-ink)",
              }}
            />
          </div>

          {/* D48/D57 + D55 — a failed sign-in stays on the page. The
              server's own message wins when there is one; the fallback
              below is what the screen says when there isn't (SignIn.dc.html's
              own commentary line, not rendered separately). Glyph + left
              rule on --nw-alert only; both text lines stay ink/muted. */}
          {(failed || passkeyFailed) && (
            <div
              role="alert"
              data-testid="login-error"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                padding: "12px 14px",
                background: "var(--nw-surface)",
                border: "1px solid var(--nw-line)",
                borderLeft: "3px solid var(--nw-alert)",
                borderRadius: "var(--nw-radius-card)",
              }}
            >
              <div className="nw-section">When it doesn't work</div>
              <div className="flex items-start" style={{ gap: "var(--nw-space-inline)" }}>
                <TriangleAlert size={14} style={{ color: "var(--nw-alert)", flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
                <div data-testid="login-error-message" style={{ fontSize: 14, lineHeight: 1.5, color: "var(--nw-ink)" }}>
                  {passkeyFailed ? PASSKEY_FAILED_MESSAGE : error || "That email and password don't match an account."}
                </div>
              </div>
              {failed && !passkeyFailed && (
                <div className="nw-help">
                  Try again, or use one of the other two doors below — neither of them needs this password.
                </div>
              )}
            </div>
          )}

          <label htmlFor="login-remember-device" className="flex items-start" style={{ gap: 10, padding: "12px 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)", cursor: "pointer" }}>
            <input
              id="login-remember-device"
              type="checkbox"
              data-testid="login-remember-device"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked === true)}
              style={{ width: 22, height: 22, flexShrink: 0, marginTop: 0, color: "var(--nw-ink)" }}
            />
            <span style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 14, lineHeight: 1.45, color: "var(--nw-ink)" }}>
              Keep me signed in on this device for 30 days.{" "}
              <span className="nw-help">
                Don't tick this on a shared or family device — anyone who picks it up could open this
                account. You can end it any time with "Sign out everywhere" in Settings.
              </span>
            </span>
          </label>

          <ActionButton
            type="submit"
            data-testid="login-submit"
            disabled={busy}
            // #944 F1 — SignIn.dc.html:145-147 draws the busy state at full
            // strength (#D4A017 fill, #0F172A label), not the kit's default
            // disabled fade (`opacity: disabled ? 0.6 : 1`). ActionButton
            // spreads `style` last, so this wins over that default without
            // touching the kit's own disabled behaviour for every OTHER
            // caller.
            style={{ opacity: 1 }}
            icon={busy ? <Loader2 size={18} className="animate-spin" aria-hidden="true" /> : null}
          >
            {busy ? "Signing in…" : "Sign in"}
          </ActionButton>
        </form>

        <div className="nw-section">Or get in without the password</div>

        {passkeySupported && (
          <button
            type="button"
            onClick={signInWithPasskey}
            disabled={busy || passkeyBusy}
            data-testid="login-passkey"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              minHeight: 48,
              background: passkeyBusy ? "var(--nw-line-faint)" : "var(--nw-surface)",
              border: "1px solid var(--nw-line)",
              color: passkeyBusy ? "var(--nw-ink-faint)" : "var(--nw-navy)",
              fontWeight: 600,
              fontSize: 15,
              borderRadius: "var(--nw-radius-control)",
              cursor: busy || passkeyBusy ? "not-allowed" : "pointer",
            }}
          >
            {passkeyBusy ? (
              <Loader2 size={18} className="animate-spin" aria-hidden="true" />
            ) : (
              <KeyRound size={18} style={{ color: "var(--nw-navy)" }} aria-hidden="true" />
            )}
            {passkeyBusy ? "Waiting for your passkey…" : "Sign in with a passkey"}
          </button>
        )}

        {/* Round 5 review, B1 — SignIn.dc.html's passkey-sheet-open block
            is `data-dc-note`, dashed border: the caption paragraph inside
            it ("The sheet is the phone's, not ours...") explains the
            state to whoever reads the drawing; it is not copy a real
            visitor is ever shown. Only the button's OWN busy label
            ("Waiting for your passkey…", above) is real UI. */}

        <Link
          to="/sign-in-link"
          data-testid="link-sign-in-link"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            minHeight: 48,
            background: "var(--nw-surface)",
            border: "1px solid var(--nw-line)",
            color: "var(--nw-navy)",
            fontWeight: 600,
            fontSize: 15,
            borderRadius: "var(--nw-radius-control)",
            textDecoration: "none",
          }}
        >
          <Mail size={18} style={{ color: "var(--nw-navy)" }} aria-hidden="true" />
          Email me a sign-in link
        </Link>

        <div style={{ display: "flex", justifyContent: "center" }}>
          <TextLink as={Link} to="/forgot-password" data-testid="link-forgot" style={{ fontSize: 13, fontWeight: 600, color: "var(--nw-navy)", textDecoration: "none" }}>
            Forgot your password?
          </TextLink>
        </div>

        <div style={{ display: "flex", justifyContent: "center", gap: 5, fontSize: 13, color: "var(--nw-ink-lede)" }}>
          <span>Don't have an account?</span>
          <Link
            to={SIGN_UP_PATH}
            state={location.state}
            data-testid="link-register"
            style={{ ...DOWNWARD_TAP_BOX, fontWeight: 700, color: "var(--nw-navy)", textDecoration: "none" }}
          >
            Create one
          </Link>
        </div>
      </div>
    </div>
  );
}
